import { endOfDay, startOfDay } from "date-fns";
import React from "react";

import { DateRangePicker, InputWrapper } from "@performyard/py-components";
import { capitalizeWord } from "@performyard/utils-isomorphic";

import EmployeePicker from "../../../components/EmployeePicker";
import { Checkboxes } from "../../../components/Inputs/SelectionOptions/Checkboxes";
import FilterPanel from "../../../components/Panel/FilterPanel";
import Spacer from "../../../components/Spacer";

import { config } from "../config";
import type { CompanyMeetingsFilters } from "../schema";

export const CompanyMeetingsFilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
	updateForm,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: CompanyMeetingsFilters) => void;
	filters: CompanyMeetingsFilters;
	updateForm: (newFilters: Partial<CompanyMeetingsFilters>) => void;
}) => {
	const statusOptions = config.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));
	const selectedStatuses = statusOptions.filter((option) => filters?.status?.includes(option.value));

	return (
		<FilterPanel
			isOpen={isOpen}
			onClose={onClose}
			title={"Filters"}
			onSaveFilters={() => {
				onApplyFilters(filters);
				onClose();
			}}
		>
			<EmployeePicker
				label="Participant"
				value={filters?.participant ?? ""}
				onChange={(newEmployeeValue) => {
					// Check that newEmployeeValue is SingleEmployeeSchema
					let singleNewEmployeeValue = newEmployeeValue;
					if (Array.isArray(newEmployeeValue)) {
						singleNewEmployeeValue = newEmployeeValue[0];
					}
					if (singleNewEmployeeValue && !("value" in singleNewEmployeeValue)) {
						return;
					}
					updateForm({ participant: singleNewEmployeeValue ?? undefined });
				}}
				isClearable
			/>
			<Spacer height={2} />
			<InputWrapper label="Meeting Date" id="meeting-date-filter">
				<DateRangePicker
					id="meeting-date-filter"
					value={{
						from: filters.dateRangeStart,
						to: filters.dateRangeEnd,
					}}
					onChange={(date) => {
						// Always set start date to beginning of day and end date to end of day
						// to ensure all datetimes within the range are captured
						const fromDate = date?.from ? startOfDay(date.from) : undefined;
						const toDate = date?.to ? endOfDay(date.to) : undefined;

						updateForm({
							dateRangeStart: fromDate,
							dateRangeEnd: toDate,
						});
					}}
					popoverContentClassName="z-[1100]"
					menuAlign="start"
					hasClearButton
				/>
			</InputWrapper>
			<Spacer height={2} />
			<Checkboxes
				label={"Status"}
				value={selectedStatuses}
				options={statusOptions}
				onChange={(options) => {
					updateForm({
						status: options.map((option) => option.value as (typeof config.statusOptions)[number]),
					});
				}}
			/>
		</FilterPanel>
	);
};

export default CompanyMeetingsFilterPanel;
